/** @jsx jsx */

/** @jsxFrag React.Fragment */
import { css, jsx } from '@emotion/react';

import * as React from 'react';

import Imgix from 'js/components/Imgix';
import { useParams } from 'js/lib/useRouter';

import { CardMetadata, CardSkills, ProductCard, breakpoints, useMediaQuery } from '@coursera/cds-core';
import { useTracker, useVisibilityTracker } from '@coursera/event-pulse/react';

import EnterpriseExperiments from 'bundles/epic/clients/Enterprise';
import type { EnterpriseBadgeWithCollection } from 'bundles/page-config-common/providers/enterprise/EnterpriseBadgeCollectionsProvider';

import DataEarlyProfessionalWide from 'images/enterprise-learning-path-description-page/data-early-professional-wide.png';
import DataEarlyProfessional from 'images/enterprise-learning-path-description-page/data-early-professional.png';
import DataProfessionalWide from 'images/enterprise-learning-path-description-page/data-professional-wide.png';
import DataProfessional from 'images/enterprise-learning-path-description-page/data-professional.png';

import _t from 'i18n!nls/product-card';

type Props = {
  enterpriseBadgeWithCollection: EnterpriseBadgeWithCollection;
  isDesktopListView?: boolean;
  itemIndex: number;
  isSkillPathway?: boolean;
};

const styles = {
  targetCardWrapper: css`
    height: 100%;

    .cds-ProductCard-list {
      border: 1px solid var(--cds-color-neutral-stroke-primary-weak);
    }
  `,
  learningPathProductCardImageWrapper: css`
    height: 100%;
    background-color: var(--cds-color-interactive-background-primary-hover-weak);
    display: flex;
    justify-content: center;
    align-items: center;
  `,
  hardCodedSkillPathwayProductCardImageWrapper: css`
    & > img {
      padding: 0;
    }
  `,
  learningPathProductCardImage: css`
    object-fit: contain !important;

    ${breakpoints.up('md')} {
      padding: var(--cds-spacing-50);
    }
  `,

  listCardContainer: css`
    /* intentionally unset min-height for list card that was default from CDS */
    min-height: unset;

    .cds-ProductCard-list {
      height: inherit;

      /* intentionally unset min-width for list card that was default from CDS */
      min-width: unset;
    }
  `,
  svg: css`
    margin-bottom: -2px;
  `,
  tagRight: css`
    .cds-ProductCard-statusTagsOverlay {
      right: 0;
      left: unset;
    }
  `,
};

export const LearningPathProductCardImage = ({ src }: { src: string }) => {
  return (
    <div css={styles.learningPathProductCardImageWrapper}>
      <img src={src} alt="" css={styles.learningPathProductCardImage} />
    </div>
  );
};

export const HardCodedSkillPathwayProductCardImage = ({ id }: { id: string }) => {
  const isWide = useMediaQuery(breakpoints.up('lg'));
  const earlyProfessionalImage = isWide ? DataEarlyProfessionalWide : DataEarlyProfessional;
  const professionalImage = isWide ? DataProfessionalWide : DataProfessional;

  return (
    <div css={styles.hardCodedSkillPathwayProductCardImageWrapper}>
      <Imgix
        src={id === 'InAf0pkmR0uwH9KZJtdLRg' ? earlyProfessionalImage : professionalImage}
        alt={_t('Data Analytics')}
        css={styles.learningPathProductCardImage}
      />
    </div>
  );
};
const EnterpriseLearningPathProductCard = ({
  enterpriseBadgeWithCollection,
  isDesktopListView,
  itemIndex,
  isSkillPathway,
}: Props) => {
  const track = useTracker();
  const { programSlug } = useParams();
  const isTabletOrSmaller = useMediaQuery(breakpoints.down('sm'));
  const shouldShowListView = isDesktopListView || isTabletOrSmaller;
  const enableVspXdpV2 = isSkillPathway && EnterpriseExperiments.get('enableVSPXDPV2');

  const visibilityTrackingRef: React.MutableRefObject<HTMLDivElement | null> = useVisibilityTracker(
    'view_product_card',
    {
      productCard: {
        index: itemIndex,
      },
      product: {
        id: enterpriseBadgeWithCollection?.enterpriseBadge?.badgeTemplateId,
        slug: enterpriseBadgeWithCollection?.enterpriseBadge?.slug,
        type: 'learning_path',
        ...(isSkillPathway ? { verifiedSkillPath: true } : {}),
      },
      enterprise: {
        ...(programSlug ? { programSlug } : {}),
      },
    }
  );

  const variantType = !shouldShowListView ? 'grid' : 'list';

  return (
    <div css={[styles.targetCardWrapper, variantType === 'list' ? styles.listCardContainer : null]}>
      <ProductCard
        ref={visibilityTrackingRef}
        data-testid="learning-path"
        productType="Learning Path"
        variant={variantType}
        css={styles.tagRight}
        title={{
          name: enterpriseBadgeWithCollection.enterpriseBadge.title,
          linkProps: {
            href: `/programs/${programSlug}/learning-path/${enterpriseBadgeWithCollection.enterpriseBadge.slug}?collectionId=${enterpriseBadgeWithCollection.enterpriseBadge.collectionId}`,
          },
        }}
        partners={[]}
        renderPreviewImage={() =>
          enableVspXdpV2 ? (
            <HardCodedSkillPathwayProductCardImage id={enterpriseBadgeWithCollection.enterpriseBadge.badgeTemplateId} />
          ) : (
            <LearningPathProductCardImage src={enterpriseBadgeWithCollection.enterpriseBadge.imageUrl} />
          )
        }
        body={
          enableVspXdpV2 && enterpriseBadgeWithCollection.enterpriseBadge.skillsMetadata.length > 0 ? (
            <CardSkills
              skills={enterpriseBadgeWithCollection.enterpriseBadge.skillsMetadata
                .map((skill) => skill?.skillName)
                .filter(Boolean)}
            />
          ) : undefined
        }
        footer={
          <>
            <CardMetadata metadata={[_t('Learning Path')]} />
          </>
        }
        onClick={() => {
          track('click_product_card', {
            productCard: {
              index: itemIndex,
            },
            product: {
              id: enterpriseBadgeWithCollection?.enterpriseBadge?.badgeTemplateId,
              slug: enterpriseBadgeWithCollection?.enterpriseBadge?.slug,
              type: 'learning_path',
              ...(isSkillPathway ? { verifiedSkillPath: true } : {}),
            },
            enterprise: {
              ...(programSlug ? { programSlug } : {}),
            },
          });
        }}
      />
    </div>
  );
};

export default EnterpriseLearningPathProductCard;
